import React, { useState } from 'react';
import i18n from '../../../i18n';
import { useSelector } from 'react-redux';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import { string, number } from 'yup';
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useFormError, validator } from 'core/form/form';

import { isUIEnabledSelector } from 'state/app/selectors';
import { catalogCategoriesSelector } from 'state/catalog/selectors';

import { Category } from './Category';
import { EditableSelectControlField } from 'components/EditableSelectControl/EditableSelectControl';
import { FormErrorMessage } from 'components/FormErrorMessage/FormErrorMessage';
import { LargeButton, LargeButtonColor } from 'components/LargeButton/LargeButton';
import { QuantityControlField } from 'components/QuantityControl/QuantityControl';
import { TextAreaControlField } from 'components/TextAreaControl/TextAreaControl';
import { SelectEnumControlField } from 'components/SelectEnumControl/SelectEnumControl';
import { TextControlField } from 'components/TextControl/TextControl';

import { TranslatableEnum } from 'components/T/GetTranslationForEnum';
import { Form } from 'forms/types';
import { CroppedArea, UnitType } from 'types';
import { OptionGroupsControlField } from 'forms/AddProductForm/OptionGroupsControl';
import { LargeHorizontalDivider } from 'components/HorizontalDivider/HorizontalDivider';
import { ReactComponent as ExpandMoreIcon } from 'assets/icons/main-16/chevron-down--16.svg';
import { Text, TextTypography } from 'components/Text/Text';
import { getCurrencyText } from '../../core/utils/utils';
import { authIsoCodeSelector } from '../../state/auth/selectors';
import css from './AddProductForm.module.css';
import { SwitchControlField } from '../../components/SwitchControl/SwitchControl';
import { GalleryControlField } from 'components/Gallery/GalleryControlField';
import { CropperMode } from 'components/CoverImageControl/types';

const addProductFormValueSelector = formValueSelector(Form.ADD_PRODUCT_FORM);
const categorySelector = (state: any) => addProductFormValueSelector(state, 'category');

interface AddProductFormOwnProps {
    onChangeCoverImageCrop: (croppedArea: CroppedArea) => void;
    // Optional
    isEditable?: boolean;
    onDelete?: () => void;
    onClose?: () => void;
}

const useStyles = makeStyles(theme => ({
    fieldParamsProductWrapper: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginLeft: theme.spacing(2),
    },
    photoContainer: {
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down(480)]: {
            marginBottom: theme.spacing(2),
        },
    },
    priceControl: {
        marginRight: theme.spacing(2),
        width: '100%',
    },
    priceWrapper: {
        display: 'flex',
        flexDirection: 'row',
    },
    quantity: {
        width: 127,
    },
    quantityControl: {
        borderRadius: 0,
        borderBottomLeftRadius: 6,
        borderTopLeftRadius: 6,
        borderRight: '1px solid rgba(15, 24, 37, 0.1);',
    },
    unit: {
        width: 95,
    },
    unitControl: {
        borderRadius: 0,
        borderBottomRightRadius: 6,
        borderTopRightRadius: 6,
    },
    marginContainer: {
        marginLeft: -theme.spacing(2),
        marginRight: -theme.spacing(2),
    },
    footer: {
        borderTop: `1px solid ${theme.palette.brandPrimary.accent}`,
        width: '100%',
        position: 'absolute',
        bottom: 0,
        left: 40,
        background: '#FDFFFC',
        zIndex: 2,
        marginLeft: -theme.spacing(5),
        marginRight: -theme.spacing(5),
        [theme.breakpoints.down(640)]: {
            left: 24,
            marginLeft: -theme.spacing(3),
            marginRight: -theme.spacing(3),
        },
        [theme.breakpoints.down(480)]: {
            left: 16,
            marginLeft: -theme.spacing(2),
            marginRight: -theme.spacing(2),
        },
    },
    footerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        padding: 20,
    },
    footerItem: {
        width: '50%',
        '&:first-child': {
            marginRight: 16,
        },
    },
    buttonDelete: {
        marginBottom: theme.spacing(7),
        [theme.breakpoints.down(480)]: {
            marginBottom: theme.spacing(8),
        },
    },
    line: {
        marginLeft: -theme.spacing(5),
        marginRight: -theme.spacing(5),
        [theme.breakpoints.down(640)]: {
            marginLeft: -theme.spacing(3),
            marginRight: -theme.spacing(3),
        },
        [theme.breakpoints.down(480)]: {
            marginLeft: -theme.spacing(2),
            marginRight: -theme.spacing(2),
        },
    },
    button: {
        '& span:hover': {
            color: '#fff',
        },
    },
}));

export const AddProductForm = reduxForm<unknown, AddProductFormOwnProps>({
    form: Form.ADD_PRODUCT_FORM,
    initialValues: {
        images: [],
        name: '',
        description: '',
        price: null as number,
        unit: UnitType.ITEM,
        category: '',
        vendorCode: '',
        optionGroups: [],
        unitQuantity: 1,
        isSpecialOffer: false,
        outOfStock: false,
        hidden: false,
    },
    validate: validator({
        name: string().required('common:validation.required'),
        price: number()
            .nullable()
            .transform(value => (isNaN(value) ? undefined : value))
            .required('common:validation.required')
            .min(0, 'common:validation.minPrice'),
        unit: string().required('common:validation.required'),
        unitQuantity: string().nullable().required('common:validation.required'),
    }),
})(props => {
    const category = useSelector(categorySelector);
    const [editCategory, setEditCategory] = useState(!!category);
    const { onChangeCoverImageCrop, handleSubmit, onClose } = props;
    const isUIEnabled = useSelector(isUIEnabledSelector);
    const { t } = i18n.useTranslation();
    const categories = useSelector(catalogCategoriesSelector);
    const isoCode = useSelector(authIsoCodeSelector);
    const error = useFormError(Form.ADD_PRODUCT_FORM);
    const classes = useStyles();

    return (
        <form onSubmit={handleSubmit}>
            <div className={classes.photoContainer}>
                <Field name="images" component={GalleryControlField} hideBannerSettings aspectRatio={1} mode={CropperMode.PRODUCT_IMAGE} cloudinaryImages={false} />
            </div>
            <Accordion elevation={0} defaultExpanded={true}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                    <div className={classes.marginContainer}>
                        <Text typography={TextTypography.LEAD_ACCENT}>{t('onboarding:card.title')}</Text>
                    </div>
                </AccordionSummary>
                <AccordionDetails>
                    <Box className={classes.marginContainer}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Field name="name" component={TextControlField} label={t('onboarding:card.name')} />
                            </Grid>
                            <Grid item xs={12}>
                                <Field
                                    name="description"
                                    component={TextAreaControlField}
                                    label={t('onboarding:card.description')}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <div className={classes.priceWrapper}>
                                    <Field
                                        name="price"
                                        component={QuantityControlField}
                                        inputClassName={classes.priceControl}
                                        label={t('onboarding:card.price') + getCurrencyText(null, isoCode)}
                                        min={0}
                                        step={1}
                                    />
                                    <div className={classes.fieldParamsProductWrapper}>
                                        <Field
                                            name="unitQuantity"
                                            component={QuantityControlField}
                                            label={t('onboarding:card.unitQuantity')}
                                            inputClassName={classes.quantityControl}
                                            className={classes.quantity}
                                            min={0}
                                            step={0.1}
                                        />
                                        <Field
                                            name="unit"
                                            component={SelectEnumControlField}
                                            enum={TranslatableEnum.UNIT_TYPE}
                                            inputClassName={classes.unitControl}
                                            className={classes.unit}
                                            items={[
                                                UnitType.ITEM,
                                                UnitType.KG,
                                                UnitType.GR,
                                                UnitType.M,
                                                UnitType.M2,
                                                UnitType.M3,
                                                UnitType.RM,
                                            ]}
                                        />
                                    </div>
                                </div>
                            </Grid>
                            <Grid item xs={12}>
                                {editCategory ? (
                                    <Field
                                        name="category"
                                        component={EditableSelectControlField}
                                        label={t('onboarding:card.category.category')}
                                        items={categories.map(c => c.name)}
                                        autoFocus={true}
                                    />
                                ) : (
                                    <div onClick={() => setEditCategory(true)}>
                                        <Category />
                                    </div>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <Field
                                    name="vendorCode"
                                    component={TextControlField}
                                    label={t('onboarding:card.vendorCode')}
                                    isDisabled={props.isEditable}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Field
                                    name="outOfStock"
                                    component={SwitchControlField}
                                    label={t('common:outOfStock')}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </AccordionDetails>
            </Accordion>
            <div className={classes.line}>
                <LargeHorizontalDivider />
            </div>
            <div className={classes.marginContainer}>
                <Field name="optionGroups" isEditable={props.isEditable} component={OptionGroupsControlField} />
            </div>

            <Box m={2} mb={8}>
                <Grid container spacing={2}>
                    {/*<Grid item xs={12}>*/}
                    {/*    <Field*/}
                    {/*        name="isSpecialOffer"*/}
                    {/*        component={SwitchControlField}*/}
                    {/*        label={t('onboarding:card.isSpecialOffer')}*/}
                    {/*    />*/}
                    {/*</Grid>*/}
                    {/*<Grid item xs={12}>*/}
                    {/*    <Field name="hidden" component={SwitchControlField} label={t('onboarding:card.hidden')} />*/}
                    {/*</Grid>*/}

                    {props.isEditable && (
                        <Grid item xs={12} className={classes.buttonDelete}>
                            <LargeButton
                                color={LargeButtonColor.ERROR}
                                onClick={() => {
                                    const confirmation = window.confirm(t('onboarding:card.confirmDelete'));
                                    if (confirmation) {
                                        props.onDelete();
                                    }
                                }}
                            >
                                {t('onboarding:card.delete')}
                            </LargeButton>
                        </Grid>
                    )}
                </Grid>
                {error && (
                    <Grid item xs={12}>
                        <Box m={2}>
                            <FormErrorMessage error={error} />
                        </Box>
                    </Grid>
                )}
            </Box>
            <footer className={classes.footer}>
                <div className={classes.footerContainer}>
                    <div className={classes.footerItem}>
                        <LargeButton onClick={onClose} color={LargeButtonColor.GRAY}>
                            <div className={classes.button}>
                                <Text typography={TextTypography.PARA_ACCENT}>{t('onboarding:card.cancel')}</Text>
                            </div>
                        </LargeButton>
                    </div>
                    <div className={classes.footerItem}>
                        <LargeButton disabled={!isUIEnabled} type="submit">
                            <div>
                                <Text typography={TextTypography.PARA_ACCENT} className={css.text_color_white}>
                                    {t(props.isEditable ? 'onboarding:card.save' : 'onboarding:card.add')}
                                </Text>
                            </div>
                        </LargeButton>
                    </div>
                </div>
            </footer>
        </form>
    );
});
